import {
    colorBaseGreyWarm50,
    layoutMarginExtraLarge,
    layoutMarginSmall,
    ProductButton,
    ProductParagraph,
    Spacing,
} from '@issuu/silkscreen';
import type { VisitorCTA as CTAName } from 'product-document-page-shared';
import React, { useContext } from 'react';
import styled from 'styled-components';

import DocumentContext from '../DocumentPage/DocumentContext';
import { OptimumLogo, PremiumLogo } from './Logos';

const Root = styled.div`
    padding: calc(${layoutMarginExtraLarge} * 2);
    margin-top: ${layoutMarginSmall};
    width: 100%;
    background-color: ${colorBaseGreyWarm50};
    grid-area: publish-cta;
`;

type CTAContent = {
    logo: JSX.Element | null;
    heading: string;
    detail: string;
    buttonLabel: string;
    buttonLink: string;
};

export const ctaContent: Record<CTAName, CTAContent> = {
    signup: {
        logo: null,
        heading: 'Millions discover their favorite reads on issuu every month.',
        detail: 'Give your content the digital home it deserves. Get it to any device in seconds.',
        buttonLabel: 'Publish for free today',
        buttonLink:
            '/publishers?entryPoint=ycpt&issuu_product=document_page&issuu_subproduct=house_ad&issuu_context=link&issuu_cta=publish_for_free_today',
    },
    upload: {
        logo: null,
        heading: 'The only thing missing from issuu is you.',
        detail: 'Upload publications in a snap. Grow your audience right away.',
        buttonLabel: 'Publish today',
        buttonLink: '/publish',
    },
    premium: {
        logo: <PremiumLogo />,
        heading: 'Build Your Brand.',
        detail: 'No banner ads next to your publication, fully custom embeds with no related content and full statistics.',
        buttonLabel: 'Upgrade now',
        buttonLink: '/upgrade',
    },
    optimum: {
        logo: <OptimumLogo />,
        heading: 'Streamline Your Workflow.',
        detail: 'Full access to Collaborate, unlimited unlisted publications and unrestricted API access.',
        buttonLabel: 'Upgrade now',
        buttonLink: '/upgrade',
    },
    partnership: {
        logo: null,
        heading: 'Give your brand a boost.',
        detail: 'Learn about sponsorship and promotional opportunities available on issuu.',
        buttonLabel: 'Find more readers',
        buttonLink: '/m/partnerships/publishers',
    },
};

const CTAButton: React.FC<{ href: string }> = ({ href, children }) => (
    <Spacing top={2}>
        <ProductButton
            onClick={() => window.open(href)}
            role="link"
            size="large"
            variant="secondary"
        >
            {children}
        </ProductButton>
    </Spacing>
);

export const VisitorCTA: React.FC = () => {
    const {
        visitor: { cta },
    } = useContext(DocumentContext);
    const { buttonLink, buttonLabel, detail, heading, logo } = ctaContent[cta];
    return (
        <Root>
            {logo}
            <ProductParagraph>
                <strong>{heading}</strong>
            </ProductParagraph>
            <ProductParagraph>{detail}</ProductParagraph>
            <CTAButton href={buttonLink}>{buttonLabel}</CTAButton>
        </Root>
    );
};
